import { Button, Grid, Group, Stack } from '@mantine/core';
import { useEffect } from 'react';
import { zodResolver } from '@mantine/form';
import { z } from 'zod';

import { MultiLangCodes, NumberInput, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

const LIMIT_DATE_DAYS_AUTHORISATION_TYPE = 'C521';

const customerViewTransitAuthSchema = z.object({
  type: z.string().min(1, { message: 'Type required' }),
  referenceNumber: z
    .string()
    .regex(/^[0-9A-Za-z-]*$/, 'Invalid reference number')
    .min(1, { message: 'Reference number required' }),
  limitDateDays: z.coerce.number().optional().nullable(),
});

type CustomerViewTransitAuthFormValues = z.infer<typeof customerViewTransitAuthSchema>;

interface EditCustomerViewTransitAuthProps {
  transitAuthEdit: { type: string; referenceNumber: string; limitDateDays?: number } | null;
  onHide(): void;
  onSave(updatedTransitAuthReference: { type: string; referenceNumber: string; limitDateDays?: number }): void;
}

export const EditCustomerViewTransitAuth: React.FC<EditCustomerViewTransitAuthProps> = (props) => {
  const { transitAuthEdit, onHide, onSave } = props;
  const codelistSystems = useCodeListSystem({ isTransit: true });

  const onSubmit = ({ type, referenceNumber, limitDateDays }: CustomerViewTransitAuthFormValues) => {
    onSave(type === LIMIT_DATE_DAYS_AUTHORISATION_TYPE ? { type, referenceNumber, limitDateDays } : { type, referenceNumber });
    onHide();
  };

  const form = useFormWithNameInputProp<CustomerViewTransitAuthFormValues>({
    initialValues: {
      type: transitAuthEdit?.type || '',
      referenceNumber: transitAuthEdit?.referenceNumber || '',
      limitDateDays: transitAuthEdit?.limitDateDays,
    },
    validate: zodResolver(customerViewTransitAuthSchema),
    validateInputOnBlur: true,
  });

  const isLimitDateDaysAllowed = form.values.type === LIMIT_DATE_DAYS_AUTHORISATION_TYPE;

  useEffect(() => {
    if (!isLimitDateDaysAllowed && form.values.limitDateDays != null) {
      form.setFieldValue('limitDateDays', null);
    }
  }, [isLimitDateDaysAllowed]);

  const saveButtonTitle = transitAuthEdit ? 'Update' : 'Add';

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack pb={20} pt={40}>
        <Grid mb="sm">
          <Grid.Col span={12}>
            <MultiLangCodes
              {...form.getInputProps('type')}
              label="Authorisation types"
              system={codelistSystems}
              code={['AuthorisationTypeDeparture', 'AuthorisationTypeDestination']}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="sm">
          <Grid.Col span={12}>
            <TextInput {...form.getInputProps('referenceNumber')} label="Reference number" />
          </Grid.Col>
        </Grid>
        <NumberInput {...form.getInputProps('limitDateDays')} label="Default limit date (days)" disabled={!isLimitDateDaysAllowed} />
      </Stack>

      <Group justify="center" mt="xl">
        <Button type="submit" key="save-modal-btn" variant="filled" size="lg">
          {saveButtonTitle}
        </Button>
      </Group>
    </form>
  );
};
